import { getImageSizeFromUrl } from '@Components/BigBrowse/BBCalculationsUtils';
import { RoundedRectangleGeometry } from '@Components/BigBrowse/GridCardMesh/Components/Shapes/RoundedRectangleGeometry';
import type { Position2D } from '@Components/BigBrowse/Model/ModelTypes';
import { useItemTransformAnimations } from '@Components/BigBrowse/bb2/meshes2/primitives/primitives.common';
import { waitForMs } from '@Utils/miscUtils';
import { useOnHoverMousePointer } from '@Utils/utilStyles';
import { a, useSpring } from '@react-spring/three';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useMemo, useRef, useState } from 'react';

import { useBbContentPresenter } from '../../BbContentPresenter';
import { useHeroLoadingTracker } from '../../HeroLoadingTracker';
import { ImageMaterial2 } from './ImageMaterial2';

type Props = {
  size: number;
  position: Position2D;
  imageUrl?: string;
  onClick?: () => void;
  transparencyColor?: string;
  children?: ReactNode;
  hoverDisabled?: boolean;
  uniqueKey: string;
  isViewHero?: boolean;
  colorOpacity?: number;
  useCache?: boolean;
};

export const ProductCardRounded = observer(function ProductCardRounded({
  size,
  position,
  onClick: onClickCallback,
  imageUrl,
  transparencyColor,
  children,
  hoverDisabled = false,
  uniqueKey,
  isViewHero,
  colorOpacity,
  useCache = false,
}: Props) {
  const bbContentPresenter = useBbContentPresenter();
  const [onIn, onOut] = useOnHoverMousePointer(
    Boolean(onClickCallback) && !hoverDisabled
  );
  const { loaded } = useHeroLoadingTracker();

  const { onPointerDown, onPointerUp, getPanDistance } =
    bbContentPresenter.canvasManager;

  const [scale, setScale] = useState(1);
  const {
    x: xAnim,
    y: yAnim,
    scale: scaleAnim,
  } = useItemTransformAnimations(uniqueKey, position, scale);

  const bmpSize = useMemo(() => getImageSizeFromUrl(imageUrl), [imageUrl]);

  const clickRef = useRef(new Date().valueOf());

  const onClick = () => {
    if (getPanDistance() >= 10) {
      // We allow a small distance to be considered as no Pan
      return;
    }

    const now = new Date().valueOf();
    if (now - clickRef.current < 500) {
      // We avoid accidental double-taps
      return;
    }

    clickRef.current = new Date().valueOf();
    onClickCallback?.();
  };

  const onImageLoad = async () => {
    await waitForMs(750); // wait for image to load and also animation finish + 250ms
    loaded.set(bbContentPresenter.paramsSignature, true);
  };

  const onPointerOver = () => {
    if (hoverDisabled) {
      return;
    }
    setScale(1.1);
    onIn();
  };

  const onPointerOut = () => {
    if (hoverDisabled) {
      return;
    }
    setScale(1);
    onOut();
  };

  const { colourOpacityAnim } = useSpring({
    to: { colourOpacityAnim: colorOpacity },
    config: { duration: 1000 },
  });

  return (
    <a.mesh
      position-x={xAnim}
      position-y={yAnim}
      position-z={0}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      frustumCulled={false}
      scale={scaleAnim}
    >
      <RoundedRectangleGeometry width={size} height={size} radius={15} />
      {imageUrl ? (
        <ImageMaterial2
          imageUrl={imageUrl}
          size={size}
          bmpSize={bmpSize}
          transparencyColor={transparencyColor}
          onImageLoad={isViewHero ? onImageLoad : undefined}
          useCache={useCache}
        />
      ) : (
        <a.meshBasicMaterial
          color='black'
          transparent={true}
          opacity={colourOpacityAnim}
        />
      )}
      {children}
    </a.mesh>
  );
});
